import { Log, type Profile, type SigninRequest, type User, UserManager, WebStorageStateStore } from 'oidc-client';
import { USER_STATE_STORAGE_KEY, type UserState, updateUserState } from '../context/user.context';
import { IDENTITY_CONFIG, METADATA_CONFIG } from '../helpers/auth-settings';
import { DEFAULT_IDP } from '../helpers/constants';

Log.logger = console;
Log.level = Log.DEBUG;

export default class AuthService {
  private static instance: AuthService | null = null;

  private UserManager: UserManager | undefined;

  /** Shared refresh, so concurrent callers exchange the refresh token once. */
  private refreshInFlight: Promise<User> | null = null;

  /**
   * Creates — or returns — the singleton AuthService instance.
   *
   * **Singleton behaviour:** If an instance already exists, the constructor
   * returns it and the provided `args` are silently ignored. The
   * `UserManager` retains the configuration from the first instantiation.
   * Call {@link AuthService._resetInstance} to discard the current instance
   * before constructing a new one with different arguments.
   *
   * @param args - Configuration options (only honoured on the first call).
   * @param args.idp - Identity provider identifier. Defaults to {@link DEFAULT_IDP}.
   */
  constructor(args = { idp: DEFAULT_IDP }) {
    if (AuthService.instance) {
      return AuthService.instance;
    }

    this.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      metadata: {
        ...METADATA_CONFIG,
      },
      extraQueryParams: { identity_provider: args.idp },
    });

    Log.logger = console;
    Log.level = Log.DEBUG;

    this.UserManager.events.addSilentRenewError((e) => {
      // biome-ignore lint/suspicious/noConsole: no telemetry path in this package yet. Tracked by MYOSH-15334
      console.warn('silent renew error', e.message);
    });
    this.UserManager.events.addAccessTokenExpired(() => {
      this.signinSilent();
    });

    AuthService.instance = this;
    return this;
  }

  signinRedirectCallback = (): Promise<User> => {
    return this.UserManager!.signinRedirectCallback();
  };

  getUser = async (): Promise<User> => {
    const user = await this.UserManager!.getUser();
    if (!user) {
      return await this.UserManager!.signinRedirectCallback();
    }
    return user;
  };

  signinRedirect = (idp?: string, redirectQueryParams?: unknown): Promise<void> => {
    if (redirectQueryParams) {
      localStorage.setItem('redirectQueryParams', JSON.stringify(redirectQueryParams));
    } else {
      // remove old query params if none added to signin request
      localStorage.removeItem('redirectQueryParams');
      localStorage.setItem('redirectPath', window.location.pathname);
    }

    // Cognito matches the IdP identifier as an exact-match (case-sensitive)
    // string, but domain-based identifiers are case-insensitive. Canonicalise to
    // lowercase here — the chokepoint for every caller, including the direct
    // /login/:idp deep link that bypasses checkIdp.
    const normalizedIdp = idp?.toLowerCase();

    // Carry the resolved idp through the OIDC `state` so it round-trips the redirect and is available at
    // the callback (via `user.state`), instead of relying on the app's pre-redirect state surviving.
    // extraQueryParams default is identity_provider myosh-default. For any other idp remove
    // identity_provider and set the domain as idp_identifier.
    const args =
      normalizedIdp && normalizedIdp !== DEFAULT_IDP
        ? { extraQueryParams: { idp_identifier: normalizedIdp }, state: { idp: normalizedIdp } }
        : { extraQueryParams: { identity_provider: DEFAULT_IDP }, state: { idp: DEFAULT_IDP } };

    return this.UserManager!.signinRedirect(args);
  };

  /**
   * Stores the user's subject identifier (sub claim) in localStorage.
   * @param profile - The user's OIDC profile containing the sub claim.
   */
  setUser = (profile: Pick<Profile, 'sub'>) => {
    return localStorage.setItem('userId', profile.sub);
  };

  /**
   * Merges freshly issued tokens into the persisted user state.
   *
   * The library keeps renewed tokens in its own `oidc.user:*` entry, which nothing in the
   * app reads — every consumer reads the `UserState` blob. Without this step a renew
   * succeeds and is silently discarded, leaving the dead token in play until a request
   * fails with a 401.
   */
  private syncUserState = (user: User) => {
    const stored = localStorage.getItem(USER_STATE_STORAGE_KEY);
    if (!stored) {
      return;
    }
    try {
      const userState = JSON.parse(stored) as UserState;
      userState.tokens = {
        ...userState.tokens,
        id_token: user.id_token,
        access_token: user.access_token,
        // Cognito only returns a refresh token when rotation is enabled; keep the existing
        // one otherwise, or the next renew has nothing to exchange.
        refresh_token: user.refresh_token || userState.tokens?.refresh_token,
        expires_at: user.expires_at,
      };
      updateUserState(userState);
    } catch {
      // Malformed stored state isn't worth tearing the session down over — the 401 path
      // still logs out if the stale token is actually rejected.
    }
  };

  signinSilent = (): Promise<User> => {
    // Share one exchange between concurrent callers. A burst of parallel 401s would
    // otherwise fire a refresh each, and a single rate-limited or rejected exchange among
    // them logs the user out even though the rest succeeded.
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.UserManager!.signinSilent()
        .then((user) => {
          // The refresh-token grant resolves with null when no stored user is found, so
          // this guards emptiness only — a renewal without a new refresh token still
          // carries fresh id and access tokens worth persisting.
          if (user) {
            this.syncUserState(user);
          }
          return user;
        })
        .catch((err) => {
          return err;
        })
        .finally(() => {
          this.refreshInFlight = null;
        });
    }
    return this.refreshInFlight;
  };

  signinSilentCallback = () => {
    return this.UserManager!.signinSilentCallback();
  };

  createSigninRequest = (): Promise<SigninRequest> => {
    return this.UserManager!.createSigninRequest();
  };

  logout = () => {
    return this.UserManager!.signoutRedirect({
      // Prevent the library from adding the standard param to avoid causing conflicts
      post_logout_redirect_uri: undefined,
      extraQueryParams: {
        client_id: IDENTITY_CONFIG.client_id,
        logout_uri: IDENTITY_CONFIG.post_logout_redirect_uri,
      },
    });
  };

  signoutRedirectCallback = async () => {
    // Caught separately: these are independent, and a failed cleanup must not stop the
    // signout response being processed.
    try {
      await this.UserManager!.clearStaleState();
    } catch {
      // Best-effort cleanup of expired sign-in state.
    }

    try {
      await this.UserManager!.signoutRedirectCallback();
    } catch {
      // Cognito's /logout does not return the `state` param the library validates, so this
      // can reject even though the session is already gone at the IdP.
    }

    // Unconditional: leaving the stored state behind is what restores an authenticated
    // session on the next load.
    localStorage.clear(); // clear all potentially locally stored items
    window.location.replace('/login');
  };

  /**
   * Set the instance to null.
   * This method is used to reset the singleton instance of AuthService.
   * It can be useful for testing purposes or when you want to create a new instance.
   * @private
   * @static
   * @returns {void}
   */
  static _resetInstance(): void {
    AuthService.instance = null;
  }
}
